import { useContext, useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import Alert from "../components/elements/Alert"
import ElemInput from "../components/elements/Input"
import Popup from "../components/elements/Popup"
import ElemSelect from "../components/elements/Select"
import Spinner from "../components/elements/Spinner"
import { Context } from "../store/store"
import { GLOBAL_ERROR_MSG, INCLUDE_AZ, INPUT_TYPES, formDefinition } from "./formdefinition"
import { allFieldsValid, scrollToErrorField } from "./formhelper"
import { createOrder } from "./order"

const ContactForm = () => {
	interface Validation {
		showValidation: boolean
	}

	const { globalState } = useContext(Context)

	const [isLoading, setIsLoading] = useState(false)
	const [address, setAddress] = useState({})
	const [validation, setValidation] = useState<Validation>({
		showValidation: false,
	})
	const [errorMsg, setErrorMsg] = useState<string>("")

	const navigate = useNavigate()

	useEffect(() => {
		// set validation states automatically, based on address states

		const addrStateColl = {}
		const valiStateColl = {}

		formDefinition.forEach(formelement => {
			if (formelement.name) {
				addrStateColl[formelement.name] = formelement.inputProps.defaultValue || ""
				// by default invalid, if there no default value defined
				valiStateColl["isInvalid_" + formelement.name] = formelement.inputProps.defaultValue ? false : true
				// overwrite for optional fields

				const isRequired = formelement.inputProps.required
				if (!isRequired) {
					valiStateColl["isInvalid_" + formelement.name] = false
				}
			}
		})

		setAddress({
			...address,
			...addrStateColl,
		})

		setValidation({
			...validation,
			...valiStateColl,
		})

		// eslint-disable-next-line
	}, [])

	/**
	 *
	 * @param e
	 * @param key
	 */
	const formChanged = (e, key) => {
		// console.log(e.target.value, key)

		// special handling for checkboxes
		if (e.target.type === "checkbox") {
			e.target.value = e.target.checked
			if (e.target.value === "false") {
				e.target.value = ""
			}
		}

		let isFieldInvalid = true
		/* prettier-ignore */
		const value = e.target.value !== "" ? e.target.value : null
		const pattern = e.target.pattern === "" || e.target.maxLength === undefined ? ".*" : e.target.pattern
		const minLength = e.target.minLength ? e.target.minLength : 0
		const maxLength = e.target.maxLength === -1 || e.target.maxLength === undefined ? 200 : e.target.maxLength

		if (value) {
			const match = value.match(pattern)
			if (match && match[0] === value) {
				if (value.length >= parseInt(minLength)) {
					if (value.length <= parseInt(maxLength)) {
						isFieldInvalid = false
					}
				}
			}
		}

		// overwrite for optional fields, if empty
		if (!!value === false && e.target.required === false) {
			isFieldInvalid = false
		}

		setAddress({
			...address,
			[key]: e.target.value,
		})

		setValidation({
			...validation,
			["isInvalid_" + key]: isFieldInvalid,
		})
	}

	/**
	 *
	 * @param e
	 */
	const contactFormSubmit = e => {
		e.preventDefault()

		setValidation({
			...validation,
			showValidation: true,
		})
		setErrorMsg("")
		if (allFieldsValid(formDefinition, validation)) {
			setIsLoading(true)

			createOrder(address, globalState.extID, updateThisState, () => scrollToErrorField(), navigate, globalState.sessionID)
		} else {
			scrollToErrorField()
		}
	}

	/**
	 *
	 * @param key
	 * @param data
	 */
	const updateThisState = (key: "UPLOADINCOMPLETE" | "PARSE_ORDER_ERRORS", data = {}) => {
		setIsLoading(false)

		if (key === "PARSE_ORDER_ERRORS") {
			setValidation({
				...validation,
				...data,
				showValidation: true,
			})
		} else if (key === "UPLOADINCOMPLETE") {
			setErrorMsg(GLOBAL_ERROR_MSG)
		}
	}

	/**
	 *
	 * @param item
	 * @param index
	 * @returns
	 */
	const getField = (item, index) => {
		if (item.type === INPUT_TYPES.SELECT) {
			const isRequired = item.inputProps.required
			let fieldProps = {
				name: item.name,
				type: "select",
				onChange: e => formChanged(e, item.name),
				defaultValue: address[item.name!],
				...item.inputProps,
			}
			if (isRequired) {
				fieldProps.required = "required"
			}
			return (
				<ElemSelect
					key={"frrForm" + index}
					label={item.label + (isRequired ? " *" : "")}
					props={fieldProps}
					hasError={validation.showValidation && validation["isInvalid_" + item.name]}
					errMsg={item.errorMessage ? item.errorMessage : null}
				>
					{item.options.map(option => (
						<option value={option.value} key={item.name + option.value}>
							{option.label}
						</option>
					))}
				</ElemSelect>
			)
		} else {
			const isRequired = item.inputProps.required
			let fieldProps = {
				name: item.name,
				type: item.type,
				onChange: e => formChanged(e, item.name),
				value: address[item.name!],
				defaultValue: address[item.name!],
				...item.inputProps,
			}
			if (isRequired) {
				fieldProps.required = "required"
			}
			return (
				<>
					<ElemInput
						key={"frrForm" + index}
						label={item.label + (isRequired ? " *" : "")}
						props={fieldProps}
						// showValidation={validation.showValidation}
						hasError={validation.showValidation && validation["isInvalid_" + item.name]}
						errMsg={item.errorMessage ? validation["isInvalid_" + item.name + "_errMsg"] || item.errorMessage : null}
					/>
				</>
			)
		}
	}

	return (
		<>
			<form noValidate autoComplete="off" onSubmit={contactFormSubmit} className="max-w-xl mx-auto ">
				{INCLUDE_AZ && <input type="hidden" id="LkzField1" value="de" />}

				<div className="grid grid-cols-1 items-center gap-3 p-4">
					<>
						{formDefinition.map((item, index) => {
							if (getField(item, index)) {
								return <div key={"frrFormDef" + index}>{getField(item, index)}</div>
							}
							return null
						})}

						{(errorMsg || "") !== "" && validation.showValidation && <Alert>{errorMsg}</Alert>}
						<div className="text-center"></div>
						{process.env.NODE_ENV === "development" && (
							<div className="mt-12 border-dashed border-2 p-8 bg-orange rounded-3xl shadow-xl">
								<h2 className="h2">Debug - Validation</h2> <p className="text-white">{JSON.stringify(validation).replaceAll(',"', ', "')}</p>
								<h2 className="h2 pt-4">Debug - Address</h2> <p className="text-white">{JSON.stringify(address).replaceAll(',"', ', "')}</p>
							</div>
						)}
					</>
					{globalState.raffledPrizeCode !== 1 && globalState.raffledPrizeCode !== 3 && globalState.raffledPrizeCode !== 6 && (
						<div className="text-sm text-white text-right">* Pflichtfelder</div>
					)}
					<button type="submit" className="btn" disabled={isLoading}>
						absenden
						{isLoading && <Spinner />}
					</button>
				</div>
			</form>
		</>
	)
}

export default ContactForm
